import React, { FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { List as ImmutableList } from 'immutable';
import { IFilterSettings } from 'common/interfaces/filter';
import { IHeadCell, ITableParams, ITableRow } from 'common/interfaces/table';
import { ILeadImt } from 'modules/crm/interfaces/leads';
import { ReactComponent as AddIcon } from 'img/icons/add.svg';
import { Button, makeStyles, Typography } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import commonMessages from 'common/messages/messages';
import tableHeaders from 'common/messages/tableHeaders';
import messages from 'modules/members/messages';
import { GuestStatus } from 'modules/front-desk/components';
import { MemberName, Table } from 'common/components';
import { IMemberPhoneItem } from 'common/components/PersonProfile/interfaces';
import { getArray } from 'common/utils/typeUtils';
import MemberPhoneItem from 'modules/members/components/MembersTable/MemberPhoneItem/MemberPhoneItem';
import usePermissionContext from 'common/hooks/context/usePermissionContext';

const useStyles = makeStyles(theme => ({
  phoneItem: {
    padding: theme.spacing(0.25, 0),
  },
}));

interface IMembersTable {
  members: ImmutableList<ILeadImt>;
  tableParams: ITableParams;
  tableFilterSettings: IFilterSettings[];
  isLoading: boolean;
  totalRows: number;
  onChangeParams: (tableParams: ITableParams) => void;
}

const MembersTable: FC<IMembersTable> = ({
  members,
  isLoading,
  totalRows,
  tableParams,
  tableFilterSettings,
  onChangeParams,
}) => {
  const navigate = useNavigate();
  const classes = useStyles();
  const intl = useIntl();
  const { MEMBER_CREATE, MEMBER_VIEW_ONE } = usePermissionContext();
  let btnCreate: JSX.Element | undefined;

  const headerOptions: IHeadCell[] = [
    {
      id: 'name',
      label: intl.formatMessage(tableHeaders.name),
      sort: true,
      padding: 'none',
    },
    { id: 'type', label: intl.formatMessage(tableHeaders.status), sort: true },
    { id: 'club', label: intl.formatMessage(tableHeaders.homeClub), sort: true },
    { id: 'membership', label: intl.formatMessage(tableHeaders.membership), sort: true },
    { id: 'phones', label: intl.formatMessage(tableHeaders.phones), sort: false },
    { id: 'email', label: intl.formatMessage(tableHeaders.emails), sort: true },
  ];

  const handleNewClick = useCallback(() => {
    navigate('/members/new');
  }, [navigate]);

  const tableRows: ITableRow[] = members
    .map(member => {
      const phones = getArray<IMemberPhoneItem>(member.get('phones')?.toJS());

      const onClickRow = MEMBER_VIEW_ONE
        ? () => navigate(`/members/${member.get('id')}`)
        : undefined;

      const phoneList = phones.map(phone => {
        return (
          <MemberPhoneItem key={phone.phone} phoneData={phone} className={classes.phoneItem} />
        );
      });

      return {
        id: String(member.get('id')),
        cells: [
          {
            label: '',
            className: 'body-avatar-cell',
            onClick: onClickRow,
            cellComponent: (
              <MemberName
                firstName={member.get('firstName') || ''}
                lastName={member.get('lastName') || ''}
                photoUrl={member.get('imageUrl')}
              />
            ),
          },
          {
            label: member.get('type') || '',
            className: 'body-cell',
            onClick: onClickRow,
            cellComponent: member.get('type') ? (
              <GuestStatus variant="body1" type={member.get('type') || ''} />
            ) : (
              ''
            ),
          },
          {
            label: member.getIn(['club', 'title']) || '',
            className: 'body-cell',
            onClick: onClickRow,
          },
          {
            label: member.getIn(['membership', 'title']) || '',
            className: 'body-cell',
            onClick: onClickRow,
          },
          {
            label: '',
            className: 'body-phones-cell',
            onClick: onClickRow,
            cellComponent: phoneList.length ? phoneList : '',
          },
          {
            label: member.get('email') || '',
            className: 'body-cell',
            onClick: onClickRow,
            cellComponent: <Typography>{member.get('email') || ''}</Typography>,
          },
        ],
      };
    })
    .toJS();

  if (MEMBER_CREATE) {
    btnCreate = (
      <Button
        variant="contained"
        color="primary"
        onClick={handleNewClick}
        startIcon={<AddIcon width={16} height={16} />}
      >
        <FormattedMessage {...commonMessages.newBtn} />
      </Button>
    );
  }

  return (
    <Table
      title={intl.formatMessage(messages.membersTitle)}
      addButton={btnCreate}
      headerOptions={headerOptions}
      showPerPageSelect
      rows={tableRows}
      isLoading={isLoading}
      filters={tableFilterSettings}
      totalRows={totalRows}
      onChangeParams={onChangeParams}
      tableParams={tableParams}
    />
  );
};

export default MembersTable;
